import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ProductConverter } from '../converters/product.converter';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ProductModel } from '../models/product.model';
import { Product } from '../../shared/DTO/product';
import { ServiceHistoryModel } from "../models/service-history.model";
import { ServiceHistoryConverter } from "../converters/service-history.converter";
import { ServiceHistory } from "../../shared/DTO/serviceHistory";
import { ModifyProductStateModel } from "../models/modifyProductState.model";
import { ModifyProductStateConverter } from "../converters/modifyProductState.converter";
import { ProductState } from "../../shared/DTO/productState";
import { ProductNoteModel } from "../models/product-note.model";
import { ProductNoteConverter } from "../converters/product-note.converter";
import { ConfigurationService } from '../../shared/services/configuration.service';
import { ServiceModel } from '../models/service.model';
import { ServiceConverter } from '../converters/service.converter';
import { OrderFileModel } from '../models/order-file.model';
import { OrderFileConverter } from '../converters/order-file.converter';
import { Attachment } from '@shared/DTO/attachment';

@Injectable()
export class ProductService {

  constructor(
    private httpClient: HttpClient,
    private productConverter: ProductConverter,
    private serviceHistoryConverter: ServiceHistoryConverter,
    private modifyProductStateConverter: ModifyProductStateConverter,
    private productNoteConverter: ProductNoteConverter,
    private configurationService: ConfigurationService,
    private serviceConverter: ServiceConverter,
    private orderFileConverter: OrderFileConverter
  ) {
  }

  // get prodotto by id
  getById(idProduct: string): Observable<ProductModel> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}`;
    return this.httpClient.get(url).pipe(
      map((dtos: Product) => {
        return this.productConverter.convertToModel(dtos);
      }),
    );
  }

  // get cronologia prodotto
  getHistoryById(idProduct: string): Observable<Array<ServiceHistoryModel>> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/servicesHistory`;
    return this.httpClient.get(url).pipe(
      map((dtos: ServiceHistory[]) => {
        return this.serviceHistoryConverter.convertToModelList(dtos);
      }),
    );
  }

  // cambia ststo prodotto
  changeState(idProduct: string, model: ModifyProductStateModel): Observable<any> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/state`;
    return this.httpClient.put(url, this.modifyProductStateConverter.convertToDto(model)).pipe(
      map((dtos: any) => {
        console.log(dtos);
        return dtos;
      }),
    );
  }

  // get stati prodotto
  getProductStates(): Observable<ProductState[]> {
    const url = this.configurationService.configuration.api.host + '/productStates';
    return this.httpClient.get(url).pipe(
      map((dtos: ProductState[]) => {
        return dtos;
      }),
    );
  }

  getProductServicesById(idProduct: string): Observable<ServiceModel[]> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/services`;
    return this.httpClient.get(url).pipe(
      map((dtos: ServiceModel[]) => {
        return this.serviceConverter.convertToModelList(dtos);
      }),
    );

  }

  //insert note
  insertNote(idProduct: string, note: ProductNoteModel): Observable<any> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/notes`;
    return this.httpClient.put(url, this.productNoteConverter.convertToDto(note)).pipe(
      map((dtos: Product) => {
        return this.productConverter.convertToModel(dtos);
      }),
    );
  }

  //insert note
  requestSendConfirmEmail(idProduct: string): Observable<any> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/send-confirm-email`;
    return this.httpClient.post(url, {});
  }

  private subject: Subject<any>;

  //insert note
  resetPassword(idProduct: string): Subject<any> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/reset-password`;
    this.subject = new Subject<any>();
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      observe: "response" as 'body' // to display the full response & as 'body' for type cast
    };
    this.httpClient.post(url, "", options)
      .subscribe((response: HttpResponse<any>) => {
        this.subject.next('CONFIRMED_PASSWORD_CHANGE');
      }, err => {
        this.subject.next(err.message);
      });
    return this.subject;
  }

  /**
   * API PER I FILE
   */

  getContract(idProduct: string): Observable<OrderFileModel> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/files/contract`;
    return this.httpClient.get(url).pipe(
      map((dtos: OrderFileModel) => {
        return this.orderFileConverter.convertToModel(dtos);
      }),
    );

  }

  getFileById(idProduct: string, idFile:string): Observable<Attachment> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/files/${idFile}`;
    return this.httpClient.get(url).pipe(
      tap((dtos: Attachment) => {
        return dtos;
      }),
    );

  }

  getAllFiles(idProduct: string): Observable<Attachment[]> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/files`;
    return this.httpClient.get(url).pipe(
      tap((dtos: Attachment[]) => {
        return dtos
      }),
    );
  }

  getAllFilesZip(idProduct: string): Observable<any> {
    const url = this.getBaseResourceEndpoint() + `/${idProduct}/files/zip`;
    let httpOptions = {
      observe: 'response' as const,
      responseType: 'arraybuffer' as const
    };
    return this.httpClient.get(url, httpOptions).pipe(
      tap((response: any) => {
        if (response.status === 200) {
          this.downloadZipBlob(response, idProduct);
        }
      }),
    );
  }

  getBaseUrl(): string {
    return this.configurationService.configuration.orchestrator.base;
  }

  private getBaseResourceEndpoint(): string {
    return (
      this.configurationService.configuration.api.products
    );
  }

  /**
   * METODI PER LA GESTIONE DEI FILE
   */

  checkIsPdf(name: string, data: string): boolean {
    const hasPdfExtension = name.toLowerCase().endsWith('.pdf');
    const isPdfContent = data.startsWith('JVBERi0xLj');
    return hasPdfExtension && isPdfContent;
  }
  getFileExtension(name: string): string {
    return name.substring(name.lastIndexOf('.') + 1);
  }
  isValidExtension(name: string): boolean {
    const validExtensions: string[] = ['jpg', 'jpeg', 'png', 'pdf']
    const fileExtension = this.getFileExtension(name).toLowerCase();
    return validExtensions.includes(fileExtension);
  }

  getMimeTypeFromFileExtension(fileExtension: string): string {
    switch (fileExtension.toLowerCase()) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'pdf':
        return 'application/pdf';
      default:
        return 'application/octet-stream';
    }
  }

  
  /**
   * Dato un base64Data, determina l'estensione e il MIME type
   */
  getFileExtensionAndMimeType(base64Data: string): { extension: string, mimeType: string } {
    if (!base64Data || base64Data.length < 5) {
      return { extension: 'bin', mimeType: 'application/octet-stream' };
    }

    // Rimuove eventuale header base64 (data:<type>;base64,)
    const cleanBase64 = base64Data.split(',').pop()?.trim() || base64Data.trim();

    // extract i primi caratteri per identificare la signature
    const signature = cleanBase64.substring(0, 10);

    if (signature.startsWith('JVBERi0xL')) {
      return { extension: 'pdf', mimeType: 'application/pdf' };
    }

    if (signature.startsWith('/9j/')) {
      return { extension: 'jpg', mimeType: 'image/jpeg' };
    }

    if (signature.startsWith('iVBORw0KG')) {
      return { extension: 'png', mimeType: 'image/png' };
    }

    if (signature.startsWith('UEsDB')) {
      return { extension: 'zip', mimeType: 'application/zip' };
    }

    if (signature.startsWith('R0lGODdh') || signature.startsWith('R0lGODlh')) {
      return { extension: 'gif', mimeType: 'image/gif' };
    }

    if (signature.startsWith('AAABAAEA') || signature.startsWith('Qk02')) {
      return { extension: 'bmp', mimeType: 'image/bmp' };
    }

    if (signature.startsWith('77u/')) {
      return { extension: 'txt', mimeType: 'text/plain' };
    }

    // fallback generic
    return { extension: 'bin', mimeType: 'application/octet-stream' };
  }
 


  convertBase64ToUint8Array(base64Data: string): Uint8Array {
    const binaryString = atob(base64Data);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  createBlobFromMimeTypeAndUint9Array(uit8Array: Uint8Array, mimeType: string,): Blob {
    return new Blob([uit8Array], { type: mimeType });
  }

  createObjectUrlFromBlob(name: string, data: string): string {
    const fileExtension = this.getFileExtension(name);
    const mimeType = this.getMimeTypeFromFileExtension(fileExtension);
    const uit8Array = this.convertBase64ToUint8Array(data);
    const blob = this.createBlobFromMimeTypeAndUint9Array(uit8Array, mimeType)
    return URL.createObjectURL(blob);
  }


  private downloadZipBlob(response: any, idProduct: string) {
    const contentDisposition = response.headers.get("Content-Disposition");
    let name;
    if (contentDisposition){
      name = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, "").trim();
    } else {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}-${hours}-${minutes}`;
      name = idProduct+'_'+ formattedDate + '.zip';
    }
    const byteArray = new Uint8Array(response.body);
    const blob = new Blob([byteArray], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    a.remove();
  }


}
